import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CAlert,
  CButton,
  CCallout,
  CCard,
  CCardBody,
  CForm,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CPasswordInput,
} from "@coreui/react-pro";
import config from "../config/config";
import { normalizeRole } from "../utils/roleMap";
import PublicAuthLayout from "../components/public/PublicAuthLayout";
import {
  extractPlatformAccessBlock,
  getPublicPlatformAccessStatus,
} from "../services/platformAccessApi";
import {
  clearStoredPlatformAccessBlock,
  readStoredPlatformAccessBlock,
} from "../utils/platformAccessClientState";
import { storeAuthenticatedUser } from "../utils/authSession";
import "../styles/public-experience.css";

const benefits = [
  {
    label: "Produktlogik",
    title: "Jobs, Qualifikationen und Unterlagen an einem Ort",
    text: "Bewerber und Arbeitgeber greifen auf dieselbe strukturierte Produktlogik für Recruiting und nächste Schritte zu.",
  },
  {
    label: "Einordnung",
    title: "Sprachstand, Wissen und Dokumente klar einordnen",
    text: "Sprachlogik, Wissenstests und vorbereitete Unterlagen bleiben nachvollziehbar statt unübersichtlich verteilt.",
  },
  {
    label: "Fortsetzung",
    title: "Recruiting- und Einwanderungsschritte geordnet fortsetzen",
    text: "Der Zugang führt in den passenden Arbeitsraum für Bewerbungen, Stellen, Tests und vorbereitete nächste Schritte.",
  },
];
const FRONTDOOR_STORAGE_KEY = "e4e_applicant_frontdoor_key";

function getSafeApplicantNextPath(value) {
  const normalized = String(value || "").trim();
  return /^\/dashboard\/applicant(?:[/?].*)?$/u.test(normalized) ? normalized : "";
}

function getSafeReturnToPath(value) {
  const normalized = String(value || "").trim();
  if (!normalized.startsWith("/")) return "";
  const [pathname] = normalized.split("?");
  if (pathname === "/stb/job-marketplace") return normalized;
  if (pathname === "/applicant/frontdoor") return normalized;
  if (/^\/dashboard\/applicant(?:\/.*)?$/u.test(pathname)) return normalized;
  return "";
}

function buildApplicantIntentTarget({ next, returnTo, intent, jobId }) {
  const applicantNextPath = getSafeApplicantNextPath(next);
  const safeReturnTo = getSafeReturnToPath(returnTo);
  if (safeReturnTo && intent === "apply" && String(jobId || "").trim()) {
    const params = new URLSearchParams();
    params.set("intent", "apply");
    params.set("jobId", String(jobId).trim());
    return `${safeReturnTo}${safeReturnTo.includes("?") ? "&" : "?"}${params.toString()}`;
  }
  if (safeReturnTo) return safeReturnTo;
  return applicantNextPath || "/dashboard/applicant";
}

function buildRegisterLink(searchParams) {
  const params = new URLSearchParams();
  const frontdoor = String(searchParams.get("frontdoor") || "").trim();
  const next = getSafeApplicantNextPath(searchParams.get("next"));
  const returnTo = getSafeReturnToPath(searchParams.get("returnTo"));
  const intent = String(searchParams.get("intent") || "").trim();
  const jobId = String(searchParams.get("jobId") || "").trim();
  const role = String(searchParams.get("role") || "").trim().toLowerCase();

  if (frontdoor) params.set("frontdoor", frontdoor);
  if (next) params.set("next", next);
  if (returnTo) params.set("returnTo", returnTo);
  if (intent === "apply" && jobId) {
    params.set("intent", "apply");
    params.set("jobId", jobId);
  }
  params.set("role", role === "employer" ? "employer" : "applicant");

  const query = params.toString();
  return query ? `/register?${query}` : "/register";
}

function mapLoginError(error, email, password) {
  if (!String(email || "").trim() || !String(password || "").trim()) {
    return "Bitte geben Sie E-Mail und Passwort ein.";
  }

  const status = Number(error?.response?.status || 0);
  const serverMessage = String(error?.response?.data?.error || "").toLowerCase();

  if (status === 400 || serverMessage.includes("missing credentials")) {
    return "Bitte geben Sie E-Mail und Passwort ein.";
  }

  if (
    status === 401 ||
    status === 403 ||
    status === 423 ||
    serverMessage.includes("invalid credentials") ||
    serverMessage.includes("account is inactive") ||
    serverMessage.includes("account is locked")
  ) {
    return "Anmeldung nicht möglich. Bitte prüfen Sie Ihre Eingaben.";
  }

  return "Der Zugang ist im Moment nicht verfügbar. Bitte versuchen Sie es erneut.";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [platformAccessNotice, setPlatformAccessNotice] = useState(null);
  const [platformPrecheck, setPlatformPrecheck] = useState(null);

  useEffect(() => {
    document.title = "Experts4Europe | Anmelden";
  }, []);

  useEffect(() => {
    const frontdoorKey = String(searchParams.get("frontdoor") || "").trim();
    if (frontdoorKey) {
      localStorage.setItem(FRONTDOOR_STORAGE_KEY, frontdoorKey);
    }
  }, [searchParams]);

  useEffect(() => {
    const routedNotice = location.state?.platformAccessBlocked || null;
    const storedNotice = readStoredPlatformAccessBlock();
    clearStoredPlatformAccessBlock();
    setPlatformAccessNotice(routedNotice || storedNotice || null);
  }, [location.state]);

  useEffect(() => {
    let active = true;
    const requestedRole = String(searchParams.get("role") || "").trim().toLowerCase();
    getPublicPlatformAccessStatus({
      action: "login",
      role: requestedRole === "applicant" || requestedRole === "employer" ? requestedRole : null,
    })
      .then((decision) => {
        if (!active) return;
        setPlatformPrecheck(decision?.allowed ? null : decision);
      })
      .catch(() => {
        if (active) setPlatformPrecheck(null);
      });
    return () => {
      active = false;
    };
  }, [searchParams]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setErrorMsg("");
    setPlatformAccessNotice(null);
    setIsLoading(true);

    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/login`, { email, password });
      const user = response.data.user;
      const role = normalizeRole(user.role);

      storeAuthenticatedUser({
        ...user,
        role,
      });
      clearStoredPlatformAccessBlock();

      const frontdoorKey = String(searchParams.get("frontdoor") || "").trim() || localStorage.getItem(FRONTDOOR_STORAGE_KEY) || "";
      const applicantNextPath = getSafeApplicantNextPath(searchParams.get("next"));
      if (role === "applicant" && frontdoorKey) {
        try {
          await axios.post(
            `${config.API_BASE_URL}/api/applicant/frontdoor/intakes/attach`,
            { external_key: frontdoorKey }
          );
        } catch (_err) {
          // Frontdoor-Attach bleibt best effort und blockiert den Login nicht.
        }
      }

      if (role === "admin") navigate("/dashboard/admin");
      else if (role === "applicant") {
        navigate(buildApplicantIntentTarget({
          next: applicantNextPath,
          returnTo: searchParams.get("returnTo"),
          intent: searchParams.get("intent"),
          jobId: searchParams.get("jobId"),
        }));
      }
      else if (role === "employer") navigate("/dashboard/employer");
      else if (role === "press") navigate("/dashboard/press");
      else setErrorMsg("Der Zugang ist im Moment nicht verfügbar. Bitte versuchen Sie es erneut.");
    } catch (error) {
      const block = extractPlatformAccessBlock(error);
      if (block) {
        setPlatformAccessNotice(block);
        setErrorMsg("");
        return;
      }
      setErrorMsg(mapLoginError(error, email, password));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setErrorMsg("");
    setPlatformAccessNotice(null);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setErrorMsg("");
    setPlatformAccessNotice(null);
  };

  const effectivePlatformBlock = platformPrecheck || platformAccessNotice;
  const loginBlocked = Boolean(
    platformPrecheck ||
    (platformAccessNotice && String(platformAccessNotice.action || "").trim().toLowerCase() === "login")
  );

  const rightPane = (
    <CCard className="public-core-auth-card">
      <CCardBody>
        <div className="public-core-auth-card__head">
          <span className="public-core-eyebrow">Zugang</span>
          <h2>Anmelden</h2>
          <p>Zugang zu Ihrem Experts4Europe-Bereich für Bewerbungen, Stellen, Tests, Dokumente und nächste Schritte.</p>
        </div>

        <div className="public-core-role-context public-core-role-context--compact">
          <strong>Klare Rollenlogik</strong>
          <p>Bewerber, Arbeitgeber und freigeschaltete interne Zugänge werden direkt in den jeweils passenden Bereich geführt.</p>
          <p className="public-core-helper-text">
            Noch unsicher? <Link to="/applicant/frontdoor?entry=unsure">Applicant Quick Check zuerst öffnen</Link>
          </p>
        </div>

        <CForm className="public-core-form" onSubmit={handleLogin}>
          {effectivePlatformBlock ? (
            <CCallout color="warning" className="public-core-alert">
              <strong>Zugang derzeit eingeschränkt</strong>
              <p className="mb-1 mt-2">{effectivePlatformBlock.message}</p>
              {effectivePlatformBlock.startsAt || effectivePlatformBlock.endsAt ? (
                <p className="mb-0 small">
                  Zeitfenster: {effectivePlatformBlock.startsAt ? new Date(effectivePlatformBlock.startsAt).toLocaleString("de-DE") : "sofort"}
                  {" - "}
                  {effectivePlatformBlock.endsAt ? new Date(effectivePlatformBlock.endsAt).toLocaleString("de-DE") : "offen"}
                  {" · "}
                  {effectivePlatformBlock.timezone || "Europe/Berlin"}
                </p>
              ) : null}
            </CCallout>
          ) : null}

          <div className="public-core-form-row">
            <CFormLabel htmlFor="login-email">E-Mail</CFormLabel>
            <CFormInput
              id="login-email"
              type="email"
              placeholder="name@beispiel.de"
              value={email}
              autoComplete="email"
              disabled={isLoading || loginBlocked}
              onChange={handleEmailChange}
              className="public-core-input"
            />
          </div>

          <div className="public-core-form-row">
            <CFormLabel htmlFor="login-password">Passwort</CFormLabel>
            <CPasswordInput
              id="login-password"
              placeholder="Passwort eingeben"
              value={password}
              autoComplete="current-password"
              disabled={isLoading || loginBlocked}
              onChange={handlePasswordChange}
              className="public-core-input"
            />
          </div>

          <div className="public-core-form-hint">
            <span>Passwort kann direkt im Feld angezeigt oder verborgen werden.</span>
            <Link to="/menue/support">Bei Zugangsproblemen</Link>
          </div>

          {errorMsg ? (
            <CAlert color="danger" className="public-core-alert">
              {errorMsg}
            </CAlert>
          ) : null}

          <div className="public-core-form-actions">
            <CLoadingButton
              type="submit"
              className="public-core-btn public-core-btn--primary public-core-btn--block"
              disabledOnLoading
              disabled={loginBlocked}
              loading={isLoading}
            >
              {isLoading ? "Anmeldung läuft..." : (loginBlocked ? "Login derzeit gesperrt" : "Login")}
            </CLoadingButton>
            <CButton
              type="button"
              className="public-core-btn public-core-btn--light public-core-btn--block"
              disabled={isLoading}
              onClick={() => navigate("/")}
            >
              Zur Startseite
            </CButton>
          </div>
        </CForm>

        <div className="public-core-card-note">
          Noch kein Konto? <Link to={buildRegisterLink(searchParams)}>Registrierung starten</Link>
        </div>
      </CCardBody>
    </CCard>
  );

  return (
    <PublicAuthLayout
      activeAuthPath="/login"
      eyebrow="Experts4Europe Zugang"
      title="Anmelden und den nächsten Schritt geordnet fortsetzen"
      description="Zugang für Bewerber, Arbeitgeber und freigeschaltete interne Bereiche. Der Login führt direkt in den jeweils passenden Experts4Europe-Bereich für Jobs, Qualifikationen, Tests, Dokumente und nächste Schritte."
      benefits={benefits}
      rightPane={rightPane}
    />
  );
}
